import * as React from "react";
import { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Popper,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import SelectRole from "./SelectRole";
import { User, Group } from "./users-types";
import { useTheme } from "@mui/material/styles";
import UsersConfirmation from "./AddConfirmationDialog";
import api from "../../pages/api/base-url";
import { mutate } from "swr";

const CustomPaper = (props: any) => {
  return <Paper elevation={8} {...props} />;
};

const CustomPopper = (props: any) => {
  const modifiers = [
    {
      name: "flip",
      options: {
        fallbackPlacements: [],
      },
    },
  ];

  return (
    <Popper
      {...props}
      modifiers={modifiers}
      popperOptions={{
        placement: "bottom",
      }}
    />
  );
};

/* SUPERUSER: Component to add a user to the app "*/

export default function AddUser(props: {
  users: User[];
  groups: Group[];
  selectedUser?: User;
  selectedGroups?: Group[];
  update?: boolean;
  open: boolean;
  setOpen: any;
}) {
  const { t } = useTranslation();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [uploadedUserInfoLoading, setUploadedUserInfoLoading] = useState(false);

  const roles = [t("ADMIN"), t("SUPERUSER"), t("USER")];
  const [user, setUser] = useState<User | null>(
    props.selectedUser && props.selectedUser !== null
      ? props.selectedUser
      : null
  );

  const [status, setStatus] = useState<string>(
    props.selectedUser ? props.selectedUser.status : ""
  );

  useEffect(() => {
    if (props.selectedUser) {
      setUser(props.selectedUser);
      setStatus(props.selectedUser.status);
    }
  }, [props.selectedUser]);

  const [groups, setGroups] = useState<Group[]>([]);

  useEffect(() => {
    const findUserGroups = async () => {
      try {
        if (props.update && props.selectedUser !== undefined) {
          const response = await api.get(
            `users/${props.selectedUser.id}/groups`
          );
          setGroups(response.data);
        }
      } catch (err: any) {
        if (err.response) {
          //Not in 200 response range
          console.log(err.response.data);
          console.log(err.response.status);
        } else {
          console.log(`Error: ${err.message}`);
        }
      }
    };
    findUserGroups();
  }, [props.selectedUser, props.update]);

  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false);

  const isConfirmButtonEnabled =
    user !== null && groups.length > 0 && status !== "";

  function resetAll() {
    setUser(null);
    setGroups([]);
    setStatus("");
  }

  const handleCloseDialog = () => {
    resetAll();
    props.setOpen(false);
  };

  const handleSelectUser = (event: any, newValue: User | null) => {
    setUser(newValue);
  };

  const handleSelectGroups = (
    event: React.SyntheticEvent,
    newValue: Group[]
  ) => {
    setGroups(newValue);
  };

  const handleSelectStatus = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  async function createUser(
    id: string,
    name: string,
    status: string,
    groups: number[]
  ) {
    try {
      setUploadedUserInfoLoading(true);
      let payload = { id: id, name: name, status: status, groups: groups };
      let res = await api.post("/users", payload);

      if (res.status >= 200 && res.status < 300) {
        mutate(`/users`);
        setIsOpenConfirmation(true);
        setUploadedUserInfoLoading(false);
      }
    } catch (err: any) {
      if (err.response) {
        //Not in 200 response range
        console.log(err.response.data);
        console.log(err.response.status);
      } else {
        console.log(`Error: ${err.message}`);
      }
    }
  }

  async function updateUser(id: string, status: string, groups: number[]) {
    try {
      setUploadedUserInfoLoading(true);
      let payload = groups;
      let res = await api.put(`/users/${id}/groups`, payload);
      let resStatus = await api.put(`/users/${id}/status/${status}`);

      if (
        res.status >= 200 &&
        res.status < 300 &&
        resStatus.status >= 200 &&
        resStatus.status < 300
      ) {
        mutate(`/users`);
        setIsOpenConfirmation(true);
        setUploadedUserInfoLoading(false);
      }
    } catch (err: any) {
      if (err.response) {
        //Not in 200 response range
        console.log(err.response.data);
        console.log(err.response.status);
      } else {
        console.log(`Error: ${err.message}`);
      }
    }
  }

  function handleSubmit() {
    if (user && groups && status !== "") {
      if (props.update) {
        setUploadedUserInfoLoading(true);
        updateUser(
          user.id,
          status,
          groups.map((group) => group.id)
        );
      } else {
        setUploadedUserInfoLoading(true);
        createUser(
          user.id,
          user.name,
          status,
          groups.map((group) => group.id)
        );
      }
    }
    handleCloseDialog();
  }

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={handleCloseDialog}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {props.update ? t("Update user") : t("Add user")}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/*Select from the INACTIVE pool
          users.filter(user => user.role === "INACTIVE")*/}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {props.update
                    ? t("Current user")
                    : t("Select the user you want to add from the list.")}
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sx={{ paddingY: 3 }}>
              {props.selectedUser === undefined ? (
                <Grid item xs={12}>
                  <Autocomplete
                    onChange={handleSelectUser}
                    value={user}
                    PaperComponent={CustomPaper}
                    PopperComponent={CustomPopper}
                    id="combo-box-demo"
                    options={props.users
                      .filter((user) => user.status === "INACTIVE")
                      .sort((a, b) => (a.name > b.name ? 1 : -1))}
                    getOptionLabel={(option) => option.name + " - " + option.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("User")}
                        placeholder={t("Users")}
                      />
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: 150,
                        border: "1px solid",
                      },
                    }}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    fullWidth
                    value={user?.name + " - " + user?.id}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Select one or more groups to add the user to.")}
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sx={{ pb: 3 }}>
              <Autocomplete
                fullWidth
                filterSelectedOptions
                multiple
                value={groups}
                limitTags={5}
                id="fixed-tags-demo"
                PaperComponent={CustomPaper}
                PopperComponent={CustomPopper}
                onChange={handleSelectGroups}
                options={props.groups.sort((a, b) =>
                  a.name > b.name ? 1 : -1
                )}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} />
                  ))
                }
                // style={{ width: 500 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Groups")}
                    placeholder={t("Groups")}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 150,
                    border: "1px solid",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Select the role of your user.")}
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sx={{ pb: 3 }}>
              <SelectRole
                handleSelection={handleSelectStatus}
                label={t("Role")}
                rolesList={roles}
                defaultValue={user?.status}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button onClick={handleSubmit} disabled={!isConfirmButtonEnabled}>
            {t("Submit")}
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        display="flex"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Dialog
          open={uploadedUserInfoLoading}
          fullScreen={fullScreen}
          fullWidth
          maxWidth="md"
        >
          <DialogContent>
            <Stack alignItems="center" justifyContent="center" spacing={2}>
              <CircularProgress thickness={3} size={80} />
              <Typography textAlign="center" variant="h5">
                {props.update ? t("Updating user...") : t("Creating user...")}
              </Typography>
            </Stack>
          </DialogContent>
        </Dialog>
      </Box>
      <UsersConfirmation
        title={
          props.update
            ? t("User update successfully!")
            : t("User created successfully!")
        }
        open={isOpenConfirmation}
        setOpen={setIsOpenConfirmation}
      />
    </div>
  );
}
